// PatchNotesViewer.tsx - User-facing patch notes button + modal viewer
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

// ============================================================================
// TYPES
// ============================================================================

interface PatchNote {
  id: string;
  version_major: number;
  version_minor: number;
  version_patch: number;
  change_type: 'bugfix' | 'feature' | 'major';
  title: string;
  body: string | null;
  created_at: string;
  updated_at: string;
  created_by_name: string | null;
}

function formatVersion(pn: PatchNote): string {
  return `${pn.version_major}.${pn.version_minor}.${pn.version_patch}`;
}

const CHANGE_TYPE_COLORS: Record<string, string> = {
  bugfix: 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30',
  feature: 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 border-cyan-500/30',
  major: 'bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-500/30',
};

// ============================================================================
// HELPERS
// ============================================================================

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function wasEdited(pn: PatchNote): boolean {
  const created = new Date(pn.created_at).getTime();
  const updated = new Date(pn.updated_at).getTime();
  return (updated - created) > 5000;
}

// ============================================================================
// MODAL
// ============================================================================

function PatchNotesModal({ onClose }: { onClose: () => void }) {
  const [patchNotes, setPatchNotes] = useState<PatchNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/patch-notes', { credentials: 'include' })
      .then(res => res.ok ? res.json() : [])
      .then((data: PatchNote[]) => {
        setPatchNotes(data);
        // Auto-expand the latest one
        if (data.length > 0) setExpandedId(data[0].id);
      })
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex flex-col"
      style={{ background: 'linear-gradient(to bottom, #0c1018 0%, #080b10 100%)' }}
    >
      {/* Glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-[120px] pointer-events-none"
        style={{ background: 'rgba(6, 182, 212, 0.08)' }}
      />

      {/* Header */}
      <div className="relative shrink-0 border-b border-[var(--c-line)]">
        <div className="max-w-3xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[var(--c-ink-3)] hover:text-[var(--c-ink)] hover:bg-[var(--c-sunken)] transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="text-sm">Back</span>
            </button>
          </div>
          <div className="space-y-1">
            <h1 className="text-3xl md:text-4xl font-bold text-[var(--c-ink)] tracking-tight">Patch Notes</h1>
            <p className="text-[var(--c-ink-3)]">What's new in Rallify</p>
          </div>
        </div>
        <div
          className="absolute bottom-0 left-0 right-0 h-px"
          style={{ background: 'linear-gradient(to right, transparent, rgba(6, 182, 212, 0.4), transparent)' }}
        />
      </div>

      {/* Content */}
      <div className="relative flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-8">
          {loading ? (
            <div className="text-center py-12 text-[var(--c-ink-4)] animate-pulse">Loading patch notes...</div>
          ) : patchNotes.length === 0 ? (
            <div className="text-center py-12 text-[var(--c-ink-3)]">No patch notes yet.</div>
          ) : (
            <div className="space-y-4">
              {patchNotes.map((pn, index) => {
                const isExpanded = expandedId === pn.id;
                const isLatest = index === 0;

                return (
                  <div
                    key={pn.id}
                    className={`rounded-xl border transition-all duration-200 ${
                      isExpanded
                        ? 'bg-[var(--c-bg-veil)] border-cyan-500/20'
                        : 'bg-[var(--c-bg-veil)] border-[var(--c-line-soft)] hover:border-[var(--c-line)]'
                    }`}
                  >
                    {/* Header - clickable to expand/collapse */}
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : pn.id)}
                      className="w-full px-5 py-4 flex items-center gap-3 text-left"
                    >
                      {/* Version badge + type */}
                      <span className={`px-2.5 py-1 text-xs font-bold rounded-lg shrink-0 ${
                        isLatest
                          ? 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 border border-cyan-500/30'
                          : 'bg-[var(--c-sunken)] text-[var(--c-ink-3)] border border-[var(--c-line)]'
                      }`}>
                        v{formatVersion(pn)}
                      </span>
                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full border shrink-0 ${CHANGE_TYPE_COLORS[pn.change_type] || ''}`}>
                        {pn.change_type === 'bugfix' ? 'Fix' : pn.change_type === 'feature' ? 'Feature' : 'Major'}
                      </span>

                      {/* Title + date */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-[var(--c-ink)] truncate">{pn.title}</h3>
                        <div className="flex items-center gap-2 text-[10px] text-[var(--c-ink-4)] mt-0.5">
                          <span>{formatDate(pn.created_at)}</span>
                          {pn.created_by_name && <span>· {pn.created_by_name}</span>}
                          {wasEdited(pn) && (
                            <span className="text-amber-700/50 dark:text-amber-400/50">· edited {formatDate(pn.updated_at)}</span>
                          )}
                        </div>
                      </div>

                      {/* NEW badge for latest */}
                      {isLatest && (
                        <span className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest rounded-full bg-green-500/20 text-green-700 dark:text-green-300 border border-green-500/30 shrink-0">
                          Latest
                        </span>
                      )}

                      {/* Expand chevron */}
                      <svg
                        className={`w-4 h-4 text-[var(--c-ink-4)] transition-transform duration-200 shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Expanded body */}
                    {isExpanded && pn.body && (
                      <div className="px-5 pb-5 pt-0">
                        <div className="border-t border-[var(--c-line-soft)] pt-4">
                        <div
                            className="patch-notes-body text-sm text-[var(--c-ink-2)] leading-relaxed"
                            style={{ lineHeight: '1.7' }}
                            dangerouslySetInnerHTML={{ __html: pn.body }}
                          />
                          <style>{`
                            .patch-notes-body ul, .patch-notes-body ol { padding-left: 1.5em; margin: 0.5em 0; }
                            .patch-notes-body ul { list-style-type: disc; }
                            .patch-notes-body ol { list-style-type: decimal; }
                            .patch-notes-body li { margin: 0.25em 0; }
                            .patch-notes-body h3 { font-size: 1em; font-weight: 700; color: rgba(255,255,255,0.9); margin: 1em 0 0.4em 0; }
                            .patch-notes-body a { color: #22d3ee; text-decoration: underline; }
                          `}</style>
                        </div>
                      </div>
                    )}

                    {/* No body message */}
                    {isExpanded && !pn.body && (
                      <div className="px-5 pb-5 pt-0">
                        <div className="border-t border-[var(--c-line-soft)] pt-4">
                          <p className="text-xs text-[var(--c-ink-4)] italic">No details provided.</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

// ============================================================================
// MAIN COMPONENT (the button)
// ============================================================================

export default function PatchNotesViewer() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)] hover:bg-[var(--c-sunken)] transition-all duration-200 group"
        title="Patch Notes"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <span className="text-[11px] font-medium tracking-wide">Patch Notes</span>
      </button>

      {open && <PatchNotesModal onClose={() => setOpen(false)} />}
    </>
  );
}
